import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, ReceiptPoundSterling } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { wpost } from '@/components/website/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const addYear = (iso) => { const d = new Date(iso || Date.now()); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };

/** Raise a 12-month hosting invoice in Xero (ProTraining) for one website-log row. */
export function HostingInvoiceDialog({ entry, defaultPrice, xeroConnected, onClose, onRaised }) {
  const [amount, setAmount] = useState('');
  const [saveDefault, setSaveDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (entry) { setAmount(String(entry.hosting_price ?? defaultPrice ?? 120)); setSaveDefault(false); } }, [entry, defaultPrice]);
  if (!entry) return null;
  const start = entry.renewal_date || new Date().toISOString().slice(0, 10);

  const raise = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const d = await wpost(`/staff/website-log/${entry.id}/hosting-invoice`, { amount: Number(amount), save_as_default: saveDefault });
      toast.success(
        <span>Draft invoice {d.invoice.number || ''} raised in Xero · renewal moved to {fmt(d.renewal_date)}{' '}
          <a href={d.invoice.url} target="_blank" rel="noreferrer" className="underline font-semibold">Open in Xero</a></span>,
        { duration: 8000 },
      );
      onRaised(); onClose();
    } catch (err) { toast.error(err.message, { duration: 8000 }); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hosting-invoice-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ReceiptPoundSterling className="w-5 h-5 text-teal-700" /> Raise hosting invoice</DialogTitle></DialogHeader>
        <form onSubmit={raise} className="space-y-4">
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
            <div className="font-bold text-gray-900">{entry.customer || entry.domain}</div>
            {entry.domain && <div className="text-gray-600">{entry.domain}</div>}
            <div className="text-xs text-gray-500 mt-1.5" data-testid="hosting-invoice-period">Covers <strong>{fmt(start)}</strong> to <strong>{fmt(addYear(start))}</strong> · Xero org: ProTraining · saved as a Draft</div>
          </div>
          {!xeroConnected && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2" data-testid="hosting-invoice-noxero">Xero isn't connected for ProTraining yet — assign the organisation in Staff → Xero Product Linking first.</p>}
          <label className="block text-xs font-semibold text-gray-700">Hosting fee for 12 months (ex VAT)
            <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">£</span>
              <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-7" required data-testid="hosting-invoice-amount" /></div>
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="hosting-invoice-save-default" /> Use £{amount || '…'} as the default for future hosting invoices</label>
          <p className="text-xs text-gray-500">Raising the invoice rolls this site's renewal date forward 12 months. If it isn't paid by the renewal date the row is flagged so you can take the site down.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !xeroConnected} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="hosting-invoice-submit">{busy ? 'Raising…' : 'Raise draft invoice'} {!busy && <ExternalLink className="w-3.5 h-3.5 ml-1.5" />}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
